// ./src/pages/private/EmailLayoutsPage/EmailLayoutsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import {
  EmailLayoutsTable,
  EmailLayoutsEditModal,
  EmailLayoutsPreviewModal,
} from "@/components/features";
import { useEmailLayouts, useNumericFeature } from "@/hooks";
import { EmailLayout } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { FEATURE_CODES } from "@/config";
import { ENTITIES } from "@/config/userRoles";

const EMAIL_LAYOUTS_BENEFITS = [
  "Create custom HTML email layouts with your brand colors, logo, and styling",
  "Use Handlebars templating for dynamic content injection",
  "Apply consistent branding across all notification emails",
  "Preview layouts with sample data before publishing",
  "Duplicate and modify system layouts as a starting point",
  "Meet brand compliance requirements for enterprise communications",
];

const VERIFICATION_NOTICE = {
  title: "Manual Verification Required",
  message: `All custom email layouts are manually reviewed before activation to prevent misuse. 
Please allow up to 48 hours (excluding weekends and holidays) for verification. 
For company-branded layouts, your account must be registered with a company email (@yourcompany.com), 
and we may contact your organization through official channels for confirmation. 
Please ensure your profile contains your correct first and last name.`,
};

export const EmailLayoutsPage: React.FC<PageProps> = (props: PageProps) => {
  const { user, hasRightTo } = useApp();
  const userId = user?.id;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ emailLayoutId?: string }>();

  // Check if email layouts feature is enabled
  const { isEnabled: isEmailLayoutsEnabled } = useNumericFeature(
    FEATURE_CODES.MAX_CUSTOM_EMAIL_LAYOUTS
  );

  // Parse URL to determine which modal to show
  const emailLayoutIdFromUrl = params.emailLayoutId;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!emailLayoutIdFromUrl && location.pathname.endsWith("/edit");
  const isPreviewMode =
    !!emailLayoutIdFromUrl && location.pathname.endsWith("/preview");

  const canCreate = hasRightTo(ENTITIES.EMAIL_LAYOUTS, OPERATION_RIGHTS.CREATE);

  // Use the email layouts hook for all data management
  const {
    emailLayouts,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    sortBy,
    isCreating,
    isUpdating,
    isPreviewing,
    isLoadingEmailLayout,
    removingEmailLayoutId,
    list,
    loadMore,
    refresh,
    get,
    preview,
    publish,
    setSortBy,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useEmailLayouts({
    autoLoad: isEmailLayoutsEnabled,
    userId,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Local UI state
  const [emailLayoutToDelete, setEmailLayoutToDelete] =
    useState<EmailLayout | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingEmailLayout, setEditingEmailLayout] =
    useState<EmailLayout | null>(null);
  const [previewingEmailLayout, setPreviewingEmailLayout] =
    useState<EmailLayout | null>(null);
  const [editedEmailLayoutId, setEditedEmailLayoutId] = useState<string | null>(
    null
  );
  const [publishingEmailLayoutId, setPublishingEmailLayoutId] = useState<
    string | null
  >(null);

  // Load email layout when in edit or preview mode
  // Always load full data as list items may not contain htmlContent, textContent, etc.
  useEffect(() => {
    if (
      (isEditMode || isPreviewMode) &&
      emailLayoutIdFromUrl &&
      isEmailLayoutsEnabled
    ) {
      get(emailLayoutIdFromUrl);
    }
  }, [
    isEditMode,
    isPreviewMode,
    emailLayoutIdFromUrl,
    get,
    isEmailLayoutsEnabled,
  ]);

  // Update editing/previewing email layout when we get data
  useEffect(() => {
    if (isEditMode && emailLayoutIdFromUrl) {
      const emailLayout = emailLayouts.find(
        (l) => l.id === emailLayoutIdFromUrl
      );
      if (emailLayout) {
        setEditingEmailLayout(emailLayout);
      }
    } else if (isPreviewMode && emailLayoutIdFromUrl) {
      const emailLayout = emailLayouts.find(
        (l) => l.id === emailLayoutIdFromUrl
      );
      if (emailLayout) {
        setPreviewingEmailLayout(emailLayout);
      }
    } else if (isAddMode && editingEmailLayout?.id) {
      // Keep the duplicated layout data for add mode
    } else if (!isAddMode && !isEditMode) {
      setEditingEmailLayout(null);
    }

    if (!isPreviewMode) {
      setPreviewingEmailLayout(null);
    }
  }, [
    isEditMode,
    isPreviewMode,
    isAddMode,
    emailLayoutIdFromUrl,
    emailLayouts,
  ]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: any = {};
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [sortBy, list]
  );

  // Handle add email layout button click
  const handleAddEmailLayout = useCallback(() => {
    clearError();
    setEditingEmailLayout(null);
    navigate("/email-layouts/add");
  }, [navigate, clearError]);

  // Handle edit email layout
  const handleEditEmailLayout = useCallback(
    (emailLayout: EmailLayout) => {
      clearError();
      if (emailLayout.id) {
        // Don't pre-populate from list as it may not have full data (htmlContent, etc.)
        // The useEffect will load full data and show skeleton while loading
        navigate(`/email-layouts/${emailLayout.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle preview email layout - opens preview modal
  const handlePreviewEmailLayout = useCallback(
    (emailLayout: EmailLayout) => {
      clearError();
      if (emailLayout.id) {
        // Don't pre-populate from list as it may not have full data
        // The preview modal will load full preview data
        navigate(`/email-layouts/${emailLayout.id}/preview`);
      }
    },
    [navigate, clearError]
  );

  // Handle duplicate email layout
  const handleDuplicateEmailLayout = useCallback(
    (emailLayout: EmailLayout) => {
      clearError();
      setEditingEmailLayout({
        ...emailLayout,
        id: undefined,
        accountId: undefined,
        name: `${emailLayout.name} (Copy)`,
      });
      navigate("/email-layouts/add");
    },
    [navigate, clearError]
  );

  // Handle publish email layout
  const handlePublishEmailLayout = useCallback(
    async (emailLayout: EmailLayout) => {
      if (emailLayout.id) {
        setPublishingEmailLayoutId(emailLayout.id);
        const result = await publish(emailLayout.id);
        setPublishingEmailLayoutId(null);
        if (result) {
          setEditedEmailLayoutId(result.id || null);
        }
      }
    },
    [publish]
  );

  // Handle remove email layout button click
  const handleRemoveEmailLayoutClick = useCallback(
    (emailLayout: EmailLayout) => {
      setEmailLayoutToDelete(emailLayout);
    },
    []
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (emailLayoutToDelete?.id) {
      const success = await remove(emailLayoutToDelete.id);
      if (success) {
        setEmailLayoutToDelete(null);
        if (emailLayoutIdFromUrl === emailLayoutToDelete.id) {
          navigate("/email-layouts");
        }
      }
    }
  }, [emailLayoutToDelete, remove, emailLayoutIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setEmailLayoutToDelete(null);
  }, []);

  // Handle email layout edit modal close
  const handleEmailLayoutEditModalClose = useCallback(() => {
    setEditingEmailLayout(null);
    clearError();
    navigate("/email-layouts");
  }, [navigate, clearError]);

  // Handle email layout preview modal close
  const handleEmailLayoutPreviewModalClose = useCallback(() => {
    setPreviewingEmailLayout(null);
    clearError();
    navigate("/email-layouts");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle email layout creation
  const handleCreateEmailLayout = useCallback(
    async (data: any) => {
      const result = await create(data);
      if (result) {
        setEditedEmailLayoutId(result.id || null);
        navigate("/email-layouts");
      }
      return result;
    },
    [create, navigate]
  );

  // Handle email layout update
  const handleUpdateEmailLayout = useCallback(
    async (emailLayoutId: string, data: any) => {
      const result = await update(emailLayoutId, data);
      if (result) {
        setEditedEmailLayoutId(result.id || null);
      }
      return result;
    },
    [update]
  );

  // Handle email layout preview request
  const handlePreviewEmailLayoutContent = useCallback(
    async (emailLayoutId: string, sampleData?: string) => {
      return await preview(emailLayoutId, sampleData);
    },
    [preview]
  );

  // Determine if edit modal should show loading
  const isEditModalLoading =
    isEditMode && emailLayoutIdFromUrl
      ? isLoadingEmailLayout(emailLayoutIdFromUrl)
      : false;

  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_CUSTOM_EMAIL_LAYOUTS}
        featureTitle="Custom Email Layouts"
        forceEnabled={isEmailLayoutsEnabled}
        featureDescription={
          <FeatureBenefitsList
            benefits={EMAIL_LAYOUTS_BENEFITS}
            notice={VERIFICATION_NOTICE}
          />
        }
      >
        <Stack gap="md">
          {/* Verification notice */}
          <Alert
            icon={<IconInfoCircle size={18} />}
            title={VERIFICATION_NOTICE.title}
            color="blue"
            variant="light"
          >
            {VERIFICATION_NOTICE.message}
          </Alert>

          {/* Page toolbar with actions and search */}
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-email-layout"
                    iconName="Plus"
                    onClick={handleAddEmailLayout}
                    disabled={
                      isCreating ||
                      !!removingEmailLayoutId ||
                      loading ||
                      !canCreate
                    }
                  >
                    Add
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={isCreating || !!removingEmailLayoutId || loading}
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "search",
                items: [
                  <SearchInput
                    key="search"
                    placeholder="Search layouts..."
                    value={searchValue}
                    onChange={(value) => handleSearchChange(value)}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
              },
            ]}
          />

          {/* Email layouts table */}
          <EmailLayoutsTable
            emailLayouts={emailLayouts}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onLoadMore={loadMore}
            onEditEmailLayout={handleEditEmailLayout}
            onDuplicateEmailLayout={handleDuplicateEmailLayout}
            onPreviewEmailLayout={handlePreviewEmailLayout}
            onRemoveEmailLayout={handleRemoveEmailLayoutClick}
            onPublishEmailLayout={handlePublishEmailLayout}
            removingEmailLayoutId={removingEmailLayoutId}
            publishingEmailLayoutId={publishingEmailLayoutId}
            editedItemId={editedEmailLayoutId}
          />

          {/* Empty state when no email layouts */}
          <EmptyState
            display={isEmpty && !loading}
            title="No email layouts yet"
            description="Create your first email layout to customize how your notification emails look"
            actionIcon="Plus"
            actionLabel="Add Layout"
            onAction={handleAddEmailLayout}
          />
        </Stack>

        {/* Email layout edit modal for create/edit */}
        <EmailLayoutsEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEmailLayoutEditModalClose}
          emailLayout={
            isEditMode
              ? editingEmailLayout
              : isAddMode && editingEmailLayout
              ? editingEmailLayout
              : null
          }
          onCreateEmailLayout={handleCreateEmailLayout}
          onUpdateEmailLayout={handleUpdateEmailLayout}
          isCreating={isCreating}
          isUpdating={isUpdating}
          isLoading={isEditModalLoading}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Email layout preview modal */}
        <EmailLayoutsPreviewModal
          opened={isPreviewMode}
          onClose={handleEmailLayoutPreviewModalClose}
          emailLayout={previewingEmailLayout}
          onPreviewEmailLayout={handlePreviewEmailLayoutContent}
          isPreviewing={isPreviewing}
          lastError={lastError}
          onClearError={clearError}
        />

        {/* Delete confirmation modal */}
        <ConfirmModal
          opened={!!emailLayoutToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Email Layout"
          message={`Are you sure you want to delete "${emailLayoutToDelete?.name}"?`}
          details="This action cannot be undone. Email templates using this layout will no longer have a layout applied."
          confirmInProgress={removingEmailLayoutId === emailLayoutToDelete?.id}
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
